/**
 * useMeetingContext - the Docs tab's meeting-context artifact (last-meeting recap + next-meeting
 * prep). Fetches on mount; `regenerate` AWAITS the server (single model call) and swaps in the
 * fresh artifact; while the SERVER reports an in-flight generation (auto-triggered by a doc
 * apply), polls every few seconds until it settles - with a safety valve so the spinner can never
 * stick if the backend dies mid-generation. Stale-while-regenerating: the last payload keeps
 * rendering under the "Regenerating…" indicator.
 */

import type { SalesDealMeetingContext } from '@protoss/shared';
import * as React from 'react';
import { salesApi } from '../services/salesApi';

/** Under the 5-min cache window; the generation is a single model call (seconds). */
const POLL_INTERVAL_MS = 3000;
const POLL_SAFETY_VALVE_MS = 3 * 60 * 1000;

export interface UseMeetingContextResult {
  context: SalesDealMeetingContext | null;
  /** First fetch only. */
  isLoading: boolean;
  /** Server-reported in-flight generation OR an optimistic manual regenerate. */
  isGenerating: boolean;
  error: string | null;
  regenerate: () => void;
  refetch: () => void;
}

export function useMeetingContext(dealUuid: string): UseMeetingContextResult {
  const [context, setContext] = React.useState<SalesDealMeetingContext | null>(null);
  const [isLoading, setIsLoading] = React.useState(false);
  const [serverGenerating, setServerGenerating] = React.useState(false);
  const [busy, setBusy] = React.useState(false); // optimistic manual regenerate
  const [error, setError] = React.useState<string | null>(null);
  const loadedOnceRef = React.useRef(false);

  const refetch = React.useCallback(() => {
    if (!dealUuid) return;
    if (!loadedOnceRef.current) setIsLoading(true);
    salesApi
      .getMeetingContext(dealUuid)
      .then(({ current, generating }) => {
        setContext(current);
        setServerGenerating(generating);
      })
      .catch((err) => console.error('[useMeetingContext] failed to load', err))
      .finally(() => {
        loadedOnceRef.current = true;
        setIsLoading(false);
      });
  }, [dealUuid]);

  React.useEffect(() => {
    loadedOnceRef.current = false;
    setContext(null);
    setServerGenerating(false);
    refetch();
  }, [refetch]);

  // Poll while the SERVER has a generation in flight (auto-triggered by a doc apply); stop when it
  // settles or after the safety valve so the indicator can never stick.
  React.useEffect(() => {
    if (!serverGenerating || !dealUuid) return;
    const startedAt = Date.now();
    const timer = window.setInterval(() => {
      if (Date.now() - startedAt > POLL_SAFETY_VALVE_MS) {
        setServerGenerating(false);
        return;
      }
      salesApi
        .getMeetingContext(dealUuid)
        .then(({ current, generating }) => {
          setContext(current);
          setServerGenerating(generating);
        })
        .catch(() => {});
    }, POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [serverGenerating, dealUuid]);

  const regenerate = React.useCallback(() => {
    if (!dealUuid || busy) return;
    setBusy(true);
    setError(null);
    salesApi
      .regenerateMeetingContext(dealUuid)
      .then(({ current }) => {
        if (current?.status === 'failed') {
          setError(current.errorMessage ?? 'Generation failed - try again');
          return;
        }
        if (current) setContext(current);
      })
      .catch((err) =>
        setError(err instanceof Error ? err.message : 'Generation failed - try again')
      )
      .finally(() => setBusy(false));
  }, [dealUuid, busy]);

  return {
    context,
    isLoading,
    isGenerating: serverGenerating || busy,
    error,
    regenerate,
    refetch,
  };
}
